/**
 * Cobro de Matrículas — migración web de ares6010.
 *
 * Registra el cobro de la matrícula de un vehículo facturado, en dos modos:
 * Préstamo (financiados, incluye contado con noprest=0) o Venta x Mayor.
 * Un chasis se puede cobrar las veces que haga falta (duplicados,
 * renovaciones, traspasos): los cobros anteriores se muestran como
 * información, con la opción de reimprimir el último recibo, pero nunca
 * impiden registrar uno nuevo. El NCF queda vacío, igual que el escritorio.
 * Se pueden cobrar varios documentos juntos (placa + matrícula...): cada
 * uno queda como su propio cobro (anulable aparte) y salen en un solo recibo.
 */
import React, { useState, useEffect } from "react";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import TextField from "@mui/material/TextField";
import MenuItem from "@mui/material/MenuItem";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import Stack from "@mui/material/Stack";
import Chip from "@mui/material/Chip";
import Radio from "@mui/material/Radio";
import RadioGroup from "@mui/material/RadioGroup";
import FormControlLabel from "@mui/material/FormControlLabel";
import Switch from "@mui/material/Switch";
import InputAdornment from "@mui/material/InputAdornment";
import Autocomplete, { createFilterOptions } from "@mui/material/Autocomplete";
import CircularProgress from "@mui/material/CircularProgress";
import SaveIcon from "@mui/icons-material/Save";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import PrintOutlinedIcon from "@mui/icons-material/PrintOutlined";
import DirectionsCarFilledOutlinedIcon from "@mui/icons-material/DirectionsCarFilledOutlined";
import PaymentsOutlinedIcon from "@mui/icons-material/PaymentsOutlined";
import CheckIcon from "@mui/icons-material/Check";
import { useGlobalMDAlert } from "context";
import { FP, PageHeader, fpInputSx } from "components/financepro";
import { useApi } from "lib/api";
import { encabezadoEmpresaHtml } from "lib/impresionhtml";
import { labelTipo, useTiposDocumento } from "lib/documentosvehiculo";

const filtrarCandidatos = createFilterOptions({
  stringify: (o) =>
    typeof o === "string"
      ? o
      : `${o.noprest} ${o.nombres || ""} ${o.chasis || ""} ${o.sucursal || ""}`,
  limit: 60,
});

const fmtMoney = (v) => {
  const n = typeof v === "number" ? v : parseFloat(String(v || "").replace(/,/g, ""));
  if (Number.isNaN(n)) return "0.00";
  return n.toLocaleString("es-DO", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const toNum = (v) => {
  const n = parseFloat(String(v ?? "").replace(/,/g, ""));
  return Number.isNaN(n) ? 0 : n;
};

const btnBase = { textTransform: "none", borderRadius: 1.8, fontWeight: 700, boxShadow: "none" };
const btnSolid = (bg, hover) => ({
  ...btnBase,
  bgcolor: bg,
  color: "#FFFFFF",
  "&:hover": { bgcolor: hover, boxShadow: "none", color: "#FFFFFF" },
  "&.Mui-disabled": { bgcolor: `${bg}66`, color: "#FFFFFF" },
});
const btnOutline = (c) => ({
  ...btnBase,
  color: c,
  bgcolor: "#FFFFFF",
  border: `1px solid ${c}`,
  "&:hover": { bgcolor: `${c}12`, border: `1px solid ${c}`, color: c },
});

const sectionBox = {
  border: `1px solid ${FP.color.border}`,
  borderRadius: 2.5,
  p: { xs: 2, sm: 2.6 },
  mb: 2.4,
  bgcolor: FP.color.cardBg,
  boxShadow: FP.shadow.soft,
};

function SectionTitle({ icon: Icon, children, color }) {
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1.2, mb: 2 }}>
      <Box
        sx={{
          width: 32,
          height: 32,
          borderRadius: 1.4,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          bgcolor: `${color}14`,
          color,
        }}
      >
        <Icon sx={{ fontSize: 19 }} />
      </Box>
      <Typography
        sx={{
          fontWeight: 800,
          fontSize: "0.74rem",
          letterSpacing: "0.1em",
          textTransform: "uppercase",
          color: FP.color.textSecondary,
        }}
      >
        {children}
      </Typography>
    </Box>
  );
}

// Recibo de Pago Documentos (mismo contenido que imprimircobromatricula).
export function imprimirReciboMatricula(r) {
  const fila = (k, v) =>
    `<tr><td style="padding:3px 8px;color:#555">${k}</td><td style="padding:3px 8px;font-weight:bold">${
      v || ""
    }</td></tr>`;
  const html = `
    <html><head><meta name="color-scheme" content="light"><title>Recibo de Pago Documentos</title></head>
    <body style="background:#fff;color:#000;font-family:Arial,sans-serif;padding:18px;font-size:13px;max-width:420px;margin:0 auto">
      ${encabezadoEmpresaHtml(r.compania)}
      <p style="text-align:center;margin:0 0 14px;font-weight:bold">Recibo de Pago Documentos</p>
      <table style="width:100%;border-collapse:collapse">
        ${r.id ? fila("Recibo #", r.id) : ""}
        ${fila("Fecha", r.fecha)}
        ${fila("Cédula", r.cedula)}
        ${fila("Cliente", r.cliente)}
        ${fila("# Préstamo", r.noprest)}
        ${fila("Chasis", r.chasis)}
        ${fila("Vehículo", [r.marca, r.modelo, r.colore].filter(Boolean).join(" · "))}
        ${r.documento ? fila("Documento", labelTipo(r.documento)) : ""}
        ${
          (r.documentos || []).length > 1
            ? r.documentos
                .map((d) => fila(labelTipo(d.documento), `RD$ ${fmtMoney(d.valor)}`))
                .join("") + fila("Total", `RD$ ${fmtMoney(r.valor)}`)
            : fila("Valor", `RD$ ${fmtMoney(r.valor)}`)
        }
        ${r.tipodepago ? fila("Tipo de pago", r.tipodepago) : ""}
        ${r.referencia ? fila("Referencia", r.referencia) : ""}
        ${fila("Descripción", r.razon)}
      </table>
      <p style="margin-top:34px;border-top:1px solid #000;padding-top:6px;text-align:center">Preparado Por</p>
      <script>window.onload=function(){window.print();}</script>
    </body></html>`;
  const iframe = document.createElement("iframe");
  iframe.style.cssText = "position:fixed;right:0;bottom:0;width:0;height:0;border:0;";
  document.body.appendChild(iframe);
  const doc = iframe.contentWindow.document;
  doc.open();
  doc.write(html);
  doc.close();
  setTimeout(() => {
    try {
      iframe.contentWindow.focus();
      iframe.contentWindow.print();
    } catch (e) {
      /* noop */
    }
    setTimeout(() => iframe.remove(), 60000);
  }, 200);
}

export default function CobroMatricula({ embedded = false, onExit, onSaved, chasisInicial } = {}) {
  const { showAlert } = useGlobalMDAlert();
  const { post } = useApi();

  const [modo, setModo] = useState("prestamo");
  const [opts, setOpts] = useState([]);
  const [buscando, setBuscando] = useState(false);
  const [sel, setSel] = useState(null);
  const [datos, setDatos] = useState(null);
  const [cargando, setCargando] = useState(false);

  const [descripcion, setDescripcion] = useState("");
  const [tipodepago, setTipodepago] = useState("Contado");
  const [referencia, setReferencia] = useState("");
  const [guardando, setGuardando] = useState(false);

  // Documentos a cobrar (catálogo tipodocumento), uno o varios a la vez,
  // cada uno con su valor. Al marcar un tipo se PROPONE su tarifa; el monto
  // tecleado después no se toca. Arranca con la matrícula marcada.
  const { tipos: tiposDocumento } = useTiposDocumento();
  const [lineas, setLineas] = useState([]); // [{ tipo, valor }]
  const [lineasListas, setLineasListas] = useState(false);
  const tarifaDe = (value) => tiposDocumento.find((t) => t.value === value)?.precio || 0;
  const lineaNueva = (value) => {
    const tarifa = tarifaDe(value);
    return { tipo: value, valor: tarifa > 0 ? String(tarifa) : "" };
  };

  const alternarDocumento = (value) =>
    setLineas((p) =>
      p.some((l) => l.tipo === value)
        ? p.filter((l) => l.tipo !== value)
        : [...p, lineaNueva(value)]
    );
  const cambiarValorLinea = (value, v) =>
    setLineas((p) => p.map((l) => (l.tipo === value ? { ...l, valor: v } : l)));

  useEffect(() => {
    if (!lineasListas && tiposDocumento.length) {
      const def = tiposDocumento.find((t) => t.value === "matricula") || tiposDocumento[0];
      setLineas([lineaNueva(def.value)]);
      setLineasListas(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tiposDocumento, lineasListas]);

  // En el orden del catálogo, no en el que se marcaron (recibo estable).
  const lineasOrdenadas = tiposDocumento
    .map((t) => lineas.find((l) => l.tipo === t.value))
    .filter(Boolean);
  const valor = lineas.reduce((s, l) => s + toNum(l.valor), 0);

  // Financiar el cobro en las cuotas del préstamo (distribución tipo ventas
  // generales). Solo disponible en modo préstamo con préstamo activo y
  // cuotas pendientes (datos.financiamiento lo dice el backend).
  const [financiar, setFinanciar] = useState(false);
  const [ncuotas, setNcuotas] = useState("");
  const financiamiento = datos?.financiamiento;
  const puedeFinanciar = Boolean(financiamiento?.disponible) && modo === "prestamo";
  const cuotasEfectivas = (() => {
    const pendientes = financiamiento?.cuotaspendientes || 0;
    const n = Math.floor(toNum(ncuotas));
    return n > 0 ? Math.min(n, pendientes) : pendientes;
  })();
  const cuotaNueva =
    puedeFinanciar && cuotasEfectivas > 0 && valor > 0
      ? (financiamiento?.valorcuota || 0) + valor / cuotasEfectivas
      : 0;

  const cargarCandidatos = (m = modo) => {
    setBuscando(true);
    return post("/api/cobromatricula_candidatos", { modo: m })
      .then(({ ok, data }) => {
        if (ok && Array.isArray(data.candidatos)) setOpts(data.candidatos);
        else
          showAlert({
            message: data.Error || "No se pudieron cargar los vehículos",
            color: "error",
          });
      })
      .finally(() => setBuscando(false));
  };

  useEffect(() => {
    cargarCandidatos();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Preselección: al abrir desde el expediente ya viene el chasis del préstamo.
  const [preselListo, setPreselListo] = useState(false);
  useEffect(() => {
    if (!chasisInicial || preselListo || !opts.length) return;
    const opcion = opts.find((x) => String(x.chasis) === String(chasisInicial));
    if (opcion) {
      setSel(opcion);
      cargarDatos(opcion);
    }
    setPreselListo(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opts]);

  const limpiar = (m = modo) => {
    setSel(null);
    setDatos(null);
    setDescripcion("");
    setTipodepago("Contado");
    setReferencia("");
    // reset de los documentos: el efecto vuelve a marcar el default con su
    // tarifa
    setLineas([]);
    setLineasListas(false);
    setFinanciar(false);
    setNcuotas("");
    cargarCandidatos(m);
  };

  const cambiarModo = (m) => {
    setModo(m);
    limpiar(m);
  };

  const cargarDatos = async (cand) => {
    setCargando(true);
    try {
      const { ok, data } = await post("/api/cobromatricula_datos", {
        modo,
        noprest: String(cand.noprest ?? "0"),
        chasis: cand.chasis,
      });
      if (!ok) {
        showAlert({ message: data.Error || "No se pudieron cargar los datos", color: "error" });
        setSel(null);
        setDatos(null);
        return;
      }
      setDatos(data);
      setFinanciar(false);
      setNcuotas("");
      const previos = (data.cobros || []).length;
      if (previos > 0) {
        showAlert({
          message:
            previos === 1
              ? "Este chasis ya tiene 1 cobro de matrícula; puedes registrar otro o reimprimir el recibo"
              : `Este chasis ya tiene ${previos} cobros de matrícula; puedes registrar otro o reimprimir el último recibo`,
          color: "info",
        });
      }
    } finally {
      setCargando(false);
    }
  };

  const reimprimir = () => {
    if (!datos?.cobro) return;
    imprimirReciboMatricula({
      fecha: datos.cobro.fecha,
      cedula: datos.cedula,
      cliente: datos.cobro.cliente,
      noprest: datos.cobro.noprest,
      chasis: datos.chasis,
      marca: datos.marca,
      modelo: datos.modelo,
      colore: datos.colore,
      valor: datos.cobro.valor,
      razon: datos.cobro.razon,
      documento: datos.cobro.tipodocumento,
      tipodepago: datos.cobro.tipodepago,
      referencia: datos.cobro.referencia,
      compania: localStorage.getItem("nombrenegocio") || "",
    });
  };

  const guardar = async () => {
    if (!sel || !datos) {
      showAlert({ message: "Seleccione un vehículo", color: "warning" });
      return;
    }
    if (!lineas.length) {
      showAlert({ message: "Seleccione al menos un documento a cobrar", color: "warning" });
      return;
    }
    const enCero = lineasOrdenadas.find((l) => toNum(l.valor) <= 0);
    if (enCero) {
      showAlert({
        message: `El valor de ${labelTipo(enCero.tipo)} no puede estar en 0`,
        color: "warning",
      });
      return;
    }
    if (!descripcion.trim()) {
      showAlert({ message: "El campo descripción no puede estar en blanco", color: "warning" });
      return;
    }
    setGuardando(true);
    try {
      const { ok, data } = await post("/api/cobromatricula_guardar", {
        modo,
        noprest: String(sel.noprest ?? "0"),
        chasis: sel.chasis,
        valor,
        documentos: lineasOrdenadas.map((l) => ({ tipodocumento: l.tipo, valor: toNum(l.valor) })),
        descripcion: descripcion.trim(),
        tipodepago,
        descripcionpago: referencia.trim(),
        financiar: puedeFinanciar && financiar,
        ncuotas: Math.floor(toNum(ncuotas)) || 0,
      });
      if (ok) {
        const nueva = data.recibo?.nuevacuota;
        showAlert({
          message:
            (data.mensajeok || "Cobro registrado") +
            (nueva ? ` — la cuota pasa a RD$ ${fmtMoney(nueva)}` : ""),
          color: "success",
        });
        if (data.recibo) imprimirReciboMatricula(data.recibo);
        limpiar();
        if (onSaved) onSaved();
      } else {
        showAlert({ message: data.Error || "Error al guardar", color: "error" });
      }
    } finally {
      setGuardando(false);
    }
  };

  return (
    <Box component="main" sx={{ minHeight: "100vh", bgcolor: FP.color.surface }}>
      <PageHeader
        title="Cobro de Matrículas"
        subtitle="Registra el cobro de la matrícula de un vehículo facturado"
        cta={
          <Stack direction="row" spacing={1.2}>
            {embedded && (
              <Button startIcon={<ArrowBackIcon />} onClick={onExit} sx={btnOutline(FP.color.navy)}>
                Volver
              </Button>
            )}
            <Button
              startIcon={<RestartAltIcon />}
              onClick={() => limpiar()}
              sx={btnOutline(FP.color.navy)}
            >
              Nuevo
            </Button>
            <Button
              startIcon={guardando ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
              onClick={guardar}
              disabled={guardando || !datos}
              sx={btnSolid(FP.color.navy, FP.color.navySoft)}
            >
              Guardar
            </Button>
          </Stack>
        }
      />

      <Box sx={{ p: { xs: 2, md: 3 } }}>
        {/* Vehículo */}
        <Box sx={sectionBox}>
          <SectionTitle icon={DirectionsCarFilledOutlinedIcon} color={FP.color.accentBlue}>
            Vehículo
          </SectionTitle>
          <RadioGroup
            row
            value={modo}
            onChange={(e) => cambiarModo(e.target.value)}
            sx={{ mb: 1.5 }}
          >
            <FormControlLabel value="prestamo" control={<Radio />} label="# Préstamo" />
            <FormControlLabel value="mayor" control={<Radio />} label="Venta x Mayor" />
          </RadioGroup>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={5}>
              <Autocomplete
                autoHighlight
                selectOnFocus
                handleHomeEndKeys
                filterOptions={filtrarCandidatos}
                options={opts}
                loading={buscando || cargando}
                value={sel}
                getOptionLabel={(o) =>
                  typeof o === "string" ? o : `${o.chasis} — ${o.nombres || ""}`
                }
                isOptionEqualToValue={(o, v) => (o.chasis || o) === (v.chasis || v)}
                onChange={(_, v) => {
                  setSel(v);
                  setDatos(null);
                  if (v?.chasis) cargarDatos(v);
                }}
                renderOption={(props, o) => (
                  <li {...props} key={`${o.chasis}-${o.noprest}`}>
                    <Box>
                      <Typography sx={{ fontWeight: 700, fontSize: "0.85rem" }}>
                        {o.chasis}
                      </Typography>
                      <Typography sx={{ fontSize: "0.72rem", color: FP.color.textMuted }}>
                        {[
                          o.noprest ? `Prest. ${o.noprest}` : "Contado",
                          o.nombres,
                          o.sucursal ? `Suc. ${o.sucursal}` : "",
                        ]
                          .filter(Boolean)
                          .join(" · ")}
                      </Typography>
                    </Box>
                  </li>
                )}
                renderInput={(params) => (
                  <TextField {...params} label="Chasis, préstamo o cliente" sx={fpInputSx} />
                )}
              />
            </Grid>
            <Grid item xs={6} sm={3} md={2}>
              <TextField
                fullWidth
                label="Cédula"
                value={datos?.cedula || ""}
                InputProps={{ readOnly: true }}
                sx={fpInputSx}
              />
            </Grid>
            <Grid item xs={6} sm={3} md={3}>
              <TextField
                fullWidth
                label="Cliente"
                value={datos ? [datos.nombres, datos.apellidos].filter(Boolean).join(" ") : ""}
                InputProps={{ readOnly: true }}
                sx={fpInputSx}
              />
            </Grid>
            <Grid item xs={6} sm={4} md={2}>
              <TextField
                fullWidth
                label="Vehículo"
                value={datos ? [datos.marca, datos.modelo].filter(Boolean).join(" ") : ""}
                InputProps={{ readOnly: true }}
                sx={fpInputSx}
              />
            </Grid>
          </Grid>
          {/* Cobros anteriores del chasis: informativos, no impiden cobrar
              de nuevo. Se listan del más reciente al más antiguo. */}
          {(datos?.cobros || []).length > 0 && (
            <Stack
              direction="row"
              spacing={1.2}
              alignItems="center"
              flexWrap="wrap"
              useFlexGap
              sx={{ mt: 1.5 }}
            >
              <Typography
                sx={{ fontWeight: 700, fontSize: "0.78rem", color: FP.color.textSecondary }}
              >
                Cobros anteriores:
              </Typography>
              {datos.cobros.map((c) => (
                <Chip
                  key={c.id}
                  size="small"
                  label={`${c.tipodocumento ? labelTipo(c.tipodocumento) + " · " : ""}${
                    c.fecha
                  } — RD$ ${fmtMoney(c.valor)}`}
                  title={c.razon || ""}
                  sx={{
                    bgcolor: FP.color.surfaceAlt,
                    color: FP.color.textSecondary,
                    fontWeight: 700,
                  }}
                />
              ))}
              <Button
                size="small"
                startIcon={<PrintOutlinedIcon />}
                onClick={reimprimir}
                sx={btnOutline(FP.color.navy)}
              >
                Reimprimir último
              </Button>
            </Stack>
          )}
        </Box>

        {/* Cobro */}
        <Box sx={{ ...sectionBox, mb: 0 }}>
          <SectionTitle icon={PaymentsOutlinedIcon} color={FP.color.success}>
            Cobro
          </SectionTitle>
          <Grid container spacing={2}>
            {/* Fila 1: qué se cobra, con el total a la derecha */}
            <Grid item xs={12}>
              <Stack
                direction={{ xs: "column", sm: "row" }}
                spacing={1.5}
                alignItems={{ xs: "flex-start", sm: "center" }}
                justifyContent="space-between"
              >
                <Box>
                  <Typography
                    sx={{
                      fontWeight: 700,
                      fontSize: "0.78rem",
                      color: FP.color.textSecondary,
                      mb: 1,
                    }}
                  >
                    Documentos a cobrar
                  </Typography>
                  <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                    {tiposDocumento.map((t) => {
                      const marcado = lineas.some((l) => l.tipo === t.value);
                      return (
                        <Chip
                          key={t.value}
                          label={t.label}
                          icon={marcado ? <CheckIcon /> : undefined}
                          onClick={() => alternarDocumento(t.value)}
                          variant={marcado ? "filled" : "outlined"}
                          sx={{
                            fontWeight: 700,
                            ...(marcado
                              ? {
                                  bgcolor: FP.color.navy,
                                  color: "#FFFFFF",
                                  "& .MuiChip-icon": { color: "#FFFFFF" },
                                  "&:hover": { bgcolor: FP.color.navySoft },
                                }
                              : { color: FP.color.textSecondary }),
                          }}
                        />
                      );
                    })}
                  </Stack>
                </Box>
                {lineas.length > 0 && (
                  <Box
                    sx={{
                      textAlign: { xs: "left", sm: "right" },
                      px: 2,
                      py: 1,
                      borderRadius: 2,
                      bgcolor: FP.color.surfaceAlt,
                      minWidth: 170,
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: "0.7rem",
                        fontWeight: 800,
                        letterSpacing: "0.08em",
                        textTransform: "uppercase",
                        color: FP.color.textMuted,
                      }}
                    >
                      Total a cobrar
                    </Typography>
                    <Typography sx={{ fontWeight: 800, fontSize: "1.15rem", color: FP.color.navy }}>
                      {`RD$ ${fmtMoney(valor)}`}
                    </Typography>
                  </Box>
                )}
              </Stack>
            </Grid>

            {/* Fila 2: el valor de cada documento marcado */}
            {lineasOrdenadas.length > 0 && (
              <Grid item xs={12}>
                <Grid container spacing={2}>
                  {lineasOrdenadas.map((l) => (
                    <Grid item xs={12} sm={6} md={3} key={l.tipo}>
                      <TextField
                        fullWidth
                        type="number"
                        label={`Valor ${labelTipo(l.tipo)}`}
                        value={l.valor}
                        onChange={(e) => cambiarValorLinea(l.tipo, e.target.value)}
                        helperText={
                          tarifaDe(l.tipo) > 0
                            ? `Tarifa: RD$ ${fmtMoney(tarifaDe(l.tipo))}`
                            : undefined
                        }
                        InputProps={{
                          startAdornment: <InputAdornment position="start">RD$</InputAdornment>,
                        }}
                        sx={fpInputSx}
                      />
                    </Grid>
                  ))}
                </Grid>
              </Grid>
            )}

            {/* Fila 3: cómo se paga */}
            <Grid item xs={12}>
              <Grid container spacing={2} alignItems="center">
                {!(puedeFinanciar && financiar) && (
                  <>
                    <Grid item xs={12} sm={6} md={3}>
                      <TextField
                        select
                        fullWidth
                        label="Tipo de pago"
                        value={tipodepago}
                        onChange={(e) => setTipodepago(e.target.value)}
                        sx={fpInputSx}
                      >
                        <MenuItem value="Contado">Contado</MenuItem>
                        <MenuItem value="Transferencias">Transferencias</MenuItem>
                        <MenuItem value="Tarjeta">Tarjeta</MenuItem>
                      </TextField>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                      <TextField
                        fullWidth
                        label="Referencia del pago"
                        value={referencia}
                        onChange={(e) => setReferencia(e.target.value)}
                        sx={fpInputSx}
                      />
                    </Grid>
                  </>
                )}
                {puedeFinanciar && (
                  <>
                    <Grid item xs={12} sm={6} md={3}>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={financiar}
                            onChange={(e) => setFinanciar(e.target.checked)}
                          />
                        }
                        label="Financiar en las cuotas"
                      />
                    </Grid>
                    {financiar && (
                      <Grid item xs={12} sm={6} md={3}>
                        <TextField
                          fullWidth
                          type="number"
                          label="Cuotas"
                          value={ncuotas}
                          onChange={(e) => setNcuotas(e.target.value)}
                          helperText={`0 = todas las pendientes (${
                            financiamiento?.cuotaspendientes || 0
                          })`}
                          sx={fpInputSx}
                        />
                      </Grid>
                    )}
                    {financiar && cuotaNueva > 0 && (
                      <Grid item xs={12}>
                        <Typography
                          sx={{ fontSize: "0.82rem", fontWeight: 700, color: FP.color.accentBlue }}
                        >
                          {`La cuota pasa de RD$ ${fmtMoney(
                            financiamiento?.valorcuota || 0
                          )} a RD$ ${fmtMoney(cuotaNueva)} durante ${cuotasEfectivas} cuota(s)`}
                        </Typography>
                      </Grid>
                    )}
                  </>
                )}
              </Grid>
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Descripción del cobro"
                value={descripcion}
                onChange={(e) => setDescripcion(e.target.value)}
                sx={fpInputSx}
              />
            </Grid>
          </Grid>
        </Box>
      </Box>
    </Box>
  );
}
